import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionIcon } from '@any-ui-react/core';
import { tv } from 'tailwind-variants';

import { TrashIcon } from '@anyx/shared-icons';


import { useConditionFields } from '../../hooks';
import { useConditionBuilderStore } from '../../store';
import { ConditionError, RuleCombinator, RuleType } from '../../types';
import { ConditionCombinator } from '../condition-combinator';
import { OperatorSelector } from '../operator-selector';

export interface RuleCardProps {
  rule: RuleType;
  type?: RuleCombinator;
  showRemove?: boolean;
  onRemove?: () => void;
  onFieldChange: (field: string) => void;
  onOperatorChange: (operator?: string) => void;
  onValuesChange: (values?: unknown[]) => void;
  error?: ConditionError | boolean;
  compact?: boolean;
  disabled?: boolean;
}

interface CompactFieldArrangeProps {
  compact: boolean;
  rule?: RuleType;
}

interface RemoveIconProps {
  onClick?: () => void;
  compact: boolean;
  disabled?: boolean;
}

const styles = tv({
  slots: {
    ruleCard: 'crm-builder__rule-card flex gap-x-2 rounded-sm border p-2',
    inputWrapper: 'relative grid w-full gap-2',
    trashIcon: '',
  },
  variants: {
    compact: {
      false: {
        ruleCard: 'border-gray-1',
        inputWrapper: 'grow grid-flow-col grid-cols-[min-content_min-content_1fr] items-center',
        trashIcon: 'self-center',
      },
      true: {
        ruleCard:
          'border-y-gray-1 first:border-t-gray-2 w-full flex-col items-start rounded-none border-y-[0.5px] p-3 first:rounded-t-sm first:border-t last:border-b',
        inputWrapper: 'grid-cols-2',
      },
    },
    error: {
      true: {},
      ruleCard: '',
    },
  },
  compoundVariants: [
    /* No compact with Error */
    {
      compact: false,
      error: true,
      class: {
        ruleCard: 'border-failure',
      },
    },
  ],
});

const CompactFieldArrange = ({
  children,
  compact,
}: PropsWithChildren<CompactFieldArrangeProps>) => {
  if (!compact) {
    return children;
  }

  return <div className="col-span-2">{children}</div>;
};

const RemoveIcon = ({ onClick, compact, disabled }: RemoveIconProps) => {
  const { trashIcon } = styles({ compact });

  return (
    <ActionIcon
      className={trashIcon()}
      disabled={disabled}
      variant="subtle"
      size="sm"
      onClick={onClick}
    >
      <TrashIcon className="h-4 w-4" />
    </ActionIcon>
  );
};

export const RuleCard = ({
  rule,
  type = RuleCombinator.IF,
  showRemove,
  onRemove,
  onFieldChange,
  onOperatorChange,
  onValuesChange,
  error,
  compact = false,
  disabled = false,
}: RuleCardProps) => {
  const fieldMap = useConditionBuilderStore((state) => state.fieldMap);

  const { ConditionSelectorComponent, OperatorSelectorComponent, ValueEditorComponent } =
    useConditionFields();

  const field = fieldMap.get(rule.field);
  const errorObj = typeof error === 'object' && error !== null ? error : undefined;

  const { t } = useTranslation('crm');
  const { ruleCard, inputWrapper } = styles({ compact, error: !!error });

  return (
    <>
      <div data-testid="rule-card" data-compact={compact} className={ruleCard()}>
        {!compact ? (
          <ConditionCombinator className="shrink-0" type={type} />
        ) : (
          <p className="mb-2">{t('crm.conditionBuilder.sendTo')}</p>
        )}

        <div className={inputWrapper()}>
          {ConditionSelectorComponent !== null && (
            <CompactFieldArrange compact={compact}>
              <ConditionSelectorComponent
                value={rule?.field}
                onSelect={onFieldChange}
                error={errorObj?.field?.message}
                disabled={disabled}
              />
            </CompactFieldArrange>
          )}
          {OperatorSelectorComponent !== null && (
            <CompactFieldArrange compact={compact} rule={rule}>
              <OperatorSelector
                compact={compact}
                operators={rule?.field ? field?.operators : []}
                value={rule?.operator}
                onSelect={onOperatorChange}
                operatorLabels={field?.operatorLabels}
                disabled={disabled}
              />
            </CompactFieldArrange>
          )}
          {rule?.field && field && (
            <CompactFieldArrange compact={compact} rule={rule}>
              {ValueEditorComponent !== null && (
                <ValueEditorComponent
                  compact={compact}
                  field={field}
                  operator={rule.operator}
                  values={rule.values}
                  onChange={onValuesChange}
                  errors={[errorObj?.values?.[0]?.message, errorObj?.values?.[1]?.message]}
                  disabled={disabled}
                />
              )}
            </CompactFieldArrange>
          )}
        </div>
        {showRemove && !compact && (
          <RemoveIcon compact={compact} onClick={onRemove} disabled={disabled} />
        )}
      </div>
      {compact && (
        <RemoveIcon compact={compact} onClick={onRemove} disabled={!showRemove || disabled} />
      )}
    </>
  );
};
